import type { ChartNode, ChartEdge } from "../../../types/api-types";

/**
 * Calculate layout positions for nodes when all nodes are at (0,0)
 * Places the most connected node in the center and arranges others in a radial pattern
 */
export const calculateLayout = (
  nodes: ChartNode[],
  edges: ChartEdge[],
): Map<string, { x: number; y: number }> => {
  const positions = new Map<string, { x: number; y: number }>();

  // Check if all nodes have default position (0,0)
  const needsLayout = nodes.every((node) => node.x === 0 && node.y === 0);

  if (!needsLayout) {
    // Use provided positions
    nodes.forEach((node) => {
      positions.set(node.id, { x: node.x, y: node.y });
    });
    return positions;
  }

  // Find the central node (node with most connections)
  const connectionCount = new Map<string, number>();
  edges.forEach((edge) => {
    connectionCount.set(
      edge.node1Id,
      (connectionCount.get(edge.node1Id) || 0) + 1,
    );
    connectionCount.set(
      edge.node2Id,
      (connectionCount.get(edge.node2Id) || 0) + 1,
    );
  });

  const sortedNodes = [...nodes].sort(
    (a, b) =>
      (connectionCount.get(b.id) || 0) - (connectionCount.get(a.id) || 0),
  );

  if (sortedNodes.length === 0) return positions;

  // Place central node in the middle
  const centerNode = sortedNodes[0];
  positions.set(centerNode.id, { x: 500, y: 400 });

  // Place other nodes in a radial pattern with moderate spacing
  const remainingNodes = sortedNodes.slice(1);
  // Moderate radius for good straight line visibility while keeping nodes closer
  const radius = Math.max(250, remainingNodes.length * 50);
  const angleStep = (2 * Math.PI) / Math.max(remainingNodes.length, 1);

  // Start angle offset to distribute nodes better
  const startAngle = -Math.PI / 2; // Start from top

  remainingNodes.forEach((node, index) => {
    const angle = startAngle + index * angleStep;
    const x = 500 + radius * Math.cos(angle);
    const y = 400 + radius * Math.sin(angle);
    positions.set(node.id, { x, y });
  });

  return positions;
};

/**
 * Get the ID of the central node (most connected node)
 */
export const getCentralNodeId = (
  nodes: ChartNode[],
  edges: ChartEdge[],
): string | null => {
  if (nodes.length === 0) return null;

  const connectionCount = new Map<string, number>();
  edges.forEach((edge) => {
    connectionCount.set(
      edge.node1Id,
      (connectionCount.get(edge.node1Id) || 0) + 1,
    );
    connectionCount.set(
      edge.node2Id,
      (connectionCount.get(edge.node2Id) || 0) + 1,
    );
  });

  const sortedNodes = [...nodes].sort(
    (a, b) =>
      (connectionCount.get(b.id) || 0) - (connectionCount.get(a.id) || 0),
  );

  return sortedNodes[0].id;
};

/**
 * Get the color hex code for an entity type
 */
export const getColorForEntityType = (entityType: string): string => {
  const type = entityType.toLowerCase();

  if (
    type.includes("person") ||
    type.includes("claimant") ||
    type.includes("driver") ||
    type.includes("individual")
  ) {
    return "#1976d2"; // Blue
  }
  if (type.includes("vehicle") || type.includes("car")) {
    return "#2e7d32"; // Green
  }
  if (type.includes("claim") || type.includes("incident")) {
    return "#d32f2f"; // Red
  }
  if (type.includes("address") || type.includes("location")) {
    return "#ed6c02"; // Orange
  }
  if (
    type.includes("company") ||
    type.includes("organisation") ||
    type.includes("organization")
  ) {
    return "#9c27b0"; // Purple
  }
  if (type.includes("phone") || type.includes("contact")) {
    return "#0288d1"; // Light Blue
  }
  if (type.includes("email")) {
    return "#00796b"; // Teal
  }
  if (type.includes("bank") || type.includes("account")) {
    return "#5d4037"; // Brown
  }
  if (type.includes("recovery")) {
    return "#388e3c"; // Dark Green
  }
  if (type.includes("reserve")) {
    return "#f57c00"; // Dark Orange
  }
  if (type.includes("transaction")) {
    return "#7b1fa2"; // Dark Purple
  }

  return "#757575"; // Grey
};

/**
 * Format a detail value for display
 */
export const formatDetailValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "—";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  return String(value);
};
